import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useAuthOptional } from '../contexts/AuthContext';
import { addSearch } from '../redux/features/savedSearchesSlice';
import { useSavedSearchAccountSync } from './useSavedSearchAccountSync';
import { useEmailAlertsStatus } from './useEmailAlertsStatus';
import { subscribeToMasterAlerts } from '../utils/email-alerts/subscribe-to-master-alerts';
import { writeMarketingConsent } from '../utils/marketing-consent/write-marketing-consent';
import { consumePendingAlertIntent, setPendingAlertIntent } from '../utils/engagement/pending-alert-intent';
import { markAlertCtaDismissedThisSession, shouldShowAlertCta } from '../utils/engagement/alert-cta-session';
import { trackEvent } from '../utils/analytics/analytics';

/**
 * Shared state machine behind every "get email alerts for this search" CTA (TEC-514 #1):
 * originally SrpAlertCta.tsx's own internals, extracted so the job-detail page's per-job
 * variant (JobDetailAlertCta.tsx) can reuse the exact same enable/guest-intent/consent flow
 * against its own synthetic target URL rather than the SRP's live querystring. `surface`
 * scopes both the per-session dismiss state and the analytics tag so the two placements never
 * interfere with each other's "seen it, dismissed it" memory.
 */
export interface UseAlertCtaCoreParams {
  /** The saved-search URL this CTA instance offers alerts for: the SRP's own current
   * querystring for SrpAlertCta, or a synthetic single-facet search URL built from a job's
   * own role/location for JobDetailAlertCta. */
  targetUrl: string;
  surface: 'srp' | 'job-detail';
  consentSource: string;
}

export interface UseAlertCtaCoreResult {
  /** False until it's safe to render anything for this CTA instance: session-dismissed,
   * auth still resolving, or (for a signed-in user) the account-sync/status reads still loading. */
  shouldRender: boolean;
  wantsMarketing: boolean;
  setWantsMarketing: (value: boolean) => void;
  busy: boolean;
  failed: boolean;
  isAuthenticated: boolean;
  alreadyActive: boolean;
  pendingConfirmation: boolean;
  uiLocale: 'en' | 'fi' | 'sv';
  handleDismiss: () => void;
  handleAuthenticatedClick: () => void;
  handleContinueAsGuest: () => void;
}

export function useAlertCtaCore({ targetUrl, surface, consentSource }: UseAlertCtaCoreParams): UseAlertCtaCoreResult {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const auth = useAuthOptional();
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const uiLocale = (['en', 'fi', 'sv'].includes(intl.locale) ? intl.locale : 'en') as 'en' | 'fi' | 'sv';

  // Starts true (not gated behind the client-only sessionStorage check) so this renders in the
  // server-rendered HTML instead of popping in after hydration: shouldShowAlertCta() needs
  // sessionStorage, which doesn't exist on the server, so waiting for it to resolve before ever
  // showing anything meant this component was structurally invisible to SSR, always. The rare
  // case (already dismissed this session) now briefly flashes then hides instead of the common
  // case (never dismissed) waiting on a client effect to appear at all.
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!shouldShowAlertCta(surface)) {
      setVisible(false);
    }
  }, [surface]);

  const [wantsMarketing, setWantsMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const savedSearches = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  const accountSync = useSavedSearchAccountSync(isAuthenticated, savedSearches);
  const emailAlertsStatus = useEmailAlertsStatus(isAuthenticated);

  const enableAlertForCurrentSearch = useCallback(
    async (url: string, shouldOptIntoMarketing: boolean) => {
      dispatch(addSearch(url));
      await accountSync.mutateItem(url, { alertEnabled: true });
      if (emailAlertsStatus.subscription?.status !== 'active') {
        await subscribeToMasterAlerts(uiLocale);
        await emailAlertsStatus.refresh();
      }
      if (shouldOptIntoMarketing) {
        await writeMarketingConsent(true, consentSource);
      }
    },
    [dispatch, accountSync, emailAlertsStatus, uiLocale, consentSource]
  );

  // Resolve an alert request made as a guest once the Auth0 redirect brings the user back:
  // shared across every surface via the one global pending-intent slot (utils/engagement/
  // pending-alert-intent.ts): whichever CTA instance's effect runs first after the auth
  // transition consumes it, and consumePendingAlertIntent() clears it so no other mounted
  // instance can double-process it.
  useEffect(() => {
    if (!isAuthenticated) return;
    const intent = consumePendingAlertIntent();
    if (!intent) return;
    setBusy(true);
    setFailed(false);
    void enableAlertForCurrentSearch(intent.url, intent.wantsMarketing)
      .catch((error) => {
        setFailed(true);
        if (import.meta.env.DEV) {
          console.error(`[alert-cta:${surface}] Failed to resolve pending alert intent`, error);
        }
      })
      .finally(() => setBusy(false));
    // Intentionally narrow: this is a one-shot resolution keyed off the auth transition, not the
    // (identity-unstable) hook results referenced inside enableAlertForCurrentSearch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    trackEvent({ name: 'job_alert_cta_interaction', params: { action: 'dismiss', surface, locale: uiLocale } });
    markAlertCtaDismissedThisSession(surface);
  }, [surface, uiLocale]);

  const handleAuthenticatedClick = useCallback(() => {
    trackEvent({ name: 'job_alert_cta_interaction', params: { action: 'enable', surface, locale: uiLocale } });
    setBusy(true);
    setFailed(false);
    void enableAlertForCurrentSearch(targetUrl, false)
      .catch((error) => {
        setFailed(true);
        if (import.meta.env.DEV) {
          console.error(`[alert-cta:${surface}] Failed to enable alerts`, error);
        }
      })
      .finally(() => setBusy(false));
  }, [enableAlertForCurrentSearch, targetUrl, surface, uiLocale]);

  /** Toggling the alert switch on while signed out: stores the intent (resolved by the effect
   * above once the Auth0 redirect brings the user back) and goes straight to sign-in. No
   * intermediate "Sign in to get job alerts" modal: the toggle itself already is the one click
   * that says what's about to happen, so a confirm-this-click modal in between was a redundant
   * extra step, not a real decision point. */
  const handleContinueAsGuest = useCallback(() => {
    trackEvent({ name: 'job_alert_cta_interaction', params: { action: 'continue_with_email', surface, locale: uiLocale } });
    setPendingAlertIntent({ url: targetUrl, wantsMarketing });
    auth?.signIn();
  }, [auth, targetUrl, wantsMarketing, surface, uiLocale]);

  const serverItem = accountSync.getServerItem(targetUrl);
  const alreadyActive =
    isAuthenticated &&
    accountSync.synced &&
    !emailAlertsStatus.loading &&
    serverItem?.alertEnabled === true &&
    emailAlertsStatus.subscription?.status === 'active';
  const pendingConfirmation =
    isAuthenticated &&
    accountSync.synced &&
    !emailAlertsStatus.loading &&
    serverItem?.alertEnabled === true &&
    emailAlertsStatus.subscription?.status === 'pendingConfirmation';

  const authSettled = Boolean(auth) && !auth?.isLoading;
  const signedInDataReady = !isAuthenticated || (accountSync.synced && !emailAlertsStatus.loading);
  const shouldRender = visible && authSettled && signedInDataReady;

  return useMemo(
    () => ({
      shouldRender,
      wantsMarketing,
      setWantsMarketing,
      busy,
      failed,
      isAuthenticated,
      alreadyActive: Boolean(alreadyActive),
      pendingConfirmation: Boolean(pendingConfirmation),
      uiLocale,
      handleDismiss,
      handleAuthenticatedClick,
      handleContinueAsGuest,
    }),
    [
      shouldRender,
      wantsMarketing,
      busy,
      failed,
      isAuthenticated,
      alreadyActive,
      pendingConfirmation,
      uiLocale,
      handleDismiss,
      handleAuthenticatedClick,
      handleContinueAsGuest,
    ]
  );
}
